/**
 * Provider-agnostic browser softphone contract. The dashboard and the extension both
 * consume the same `Softphone` UI + `useSoftphone` hook; only the concrete client
 * (Telnyx / Twilio / JustCall) and the injected mic strategy differ per surface.
 */

export type SoftphoneStatus = 'idle' | 'connecting' | 'ringing' | 'active' | 'ended' | 'error';

/**
 * The carriers the browser softphone can speak to. A local copy of the shared list in
 * `@poseidon/types/svr/voiceProvider` — UI components may not import svr types; the app wrappers'
 * values assign structurally.
 */
export type SoftphoneProvider = 'telnyx' | 'twilio' | 'justcall';

/**
 * SIP custom header that carries our `voice_call.id` on the outbound call so the server
 * webhook can correlate back to the row. MUST match the server constant in
 * `packages/server/src/api/services/voice/voiceProvider.types.ts`.
 */
export const VOICE_CALL_ID_HEADER = 'X-Voice-Call-Id';

/**
 * The carrier refused to place the call (it ended before it ever rang, and the server's status
 * callback says `failed`). `code` is the carrier's own error number — Twilio 13225 "call blocked by
 * Twilio" is the account-level block the notice is built around — or null when the carrier filed
 * no code. Distinct from the client-side `error` status: the WebRTC leg was fine, the PSTN bridge
 * was rejected, which the browser otherwise can't tell apart from "nobody answered".
 */
export interface SoftphoneCarrierFailure {
  code: number | null;
}

/** How the carrier settled a call, as the server recorded it from the status callback. */
export interface SoftphoneCallResult {
  /** Normalized terminal status ('completed' | 'no_answer' | 'busy' | 'failed' | 'canceled'), or null until the callback lands. */
  callStatus: string | null;
  carrierErrorCode: number | null;
  /**
   * The disposition the server derived on its own from the carrier verdict / answering-machine
   * detection / talk time (an outcome key, e.g. 'no_answer', 'voicemail', 'connected'), or null
   * when the call is ambiguous and the rep must pick. Optional so an older server is still valid.
   */
  autoOutcome?: string | null;
}

/**
 * A rolled-up picture of the media quality of one call, built in the browser from the SDK's own
 * per-second RTC samples and the warnings it raised. Attached to the call's termination
 * diagnostic so "the audio cut out" has data behind it: which warnings fired, how often the
 * media path dropped and reconnected, and how bad loss / jitter / latency got.
 *
 * Twilio's own copy of this (Voice Insights) is off for our account, so this is the only record.
 */
export interface SoftphoneCallQuality {
  /**
   * True when anything a rep could hear went wrong: a quality warning was raised or the media
   * path had to reconnect. A clean call is false even if it ended abnormally.
   */
  degraded: boolean;
  /** Every warning the SDK raised, deduped, in order of first occurrence (e.g. `high-packet-loss`). */
  warnings: string[];
  /** Warnings that were still active when the call ended (raised, never cleared). */
  warningsActiveAtEnd: string[];
  /** How many times the media or signaling path dropped and the SDK had to reconnect. */
  reconnects: number;
  /** Total seconds the call spent in the SDK's reconnecting state — silence on both ends. */
  reconnectingSec: number;
  /** Number of per-second RTC samples the summary is built from (0 when none arrived). */
  samples: number;
  /** Packet loss across the call as a percentage of packets, or null without samples. */
  packetsLostPct: { avg: number; max: number } | null;
  /** Inbound jitter in milliseconds, or null without samples. */
  jitterMs: { avg: number; max: number } | null;
  /** Round-trip time in milliseconds, or null without samples. */
  rttMs: { avg: number; max: number } | null;
  /** The SDK's own Mean Opinion Score estimate (1–5), or null when it never computed one. */
  mos: { avg: number; min: number } | null;
  /** The negotiated audio codec (e.g. `PCMU`, `opus`), or null when no sample reported one. */
  codec: string | null;
  /** The Twilio edge the Device was connected through (e.g. `ashburn`), or null if unknown. */
  edge: string | null;
  /** The label of the microphone the call used (e.g. `AirPods Pro`), or null when unknown. */
  inputDevice: string | null;
  /** How many times the browser's audio device list changed during the call (headset in / out). */
  deviceChanges: number;
  /**
   * How many of those changes took away the device the call was using — the SDK then falls back
   * to the default mic, which is what a Bluetooth headset dropping mid-call looks like.
   */
  activeDeviceLost: number;
  /**
   * The longest run of consecutive seconds in which the rep's mic level did not change at all
   * while unmuted. The SDK raises `constant-audio-input-level` at 10; this keeps the actual
   * length, including runs shorter than the SDK's threshold.
   */
  longestFlatInputSec: number;
  /**
   * The longest run of consecutive seconds in which the audio coming FROM the prospect did not
   * change at all. The SDK measures this but never emits it to listeners (only to Voice Insights),
   * so this is the only client-side signal for "the other side went dead". A quiet prospect
   * produces short runs; a dead inbound stream produces one long one.
   */
  longestFlatOutputSec: number;
}

/**
 * How a call ended, in the carrier's own terms — the detail that explains a failure but has no
 * place in the UI. Emitted for every terminal call event and every device-level failure.
 *
 * This exists because the codes that diagnose a broken call (Twilio 31000 TwiML fetch failure,
 * 13214 invalid caller id surfacing as an instant hangup) were previously written to
 * `console.error` and nowhere else: the browser console is not collected, so a production
 * calling outage left no trace on any server and had to be reconstructed from carrier logs by
 * hand. The surface supplies the sink — the dashboard reports to Sentry, the extension can log
 * or ignore — so this package stays free of any reporter dependency.
 *
 * Deliberately separate from `SoftphoneCarrierFailure` above, which is the REP-facing story and
 * comes from the server's status callback. This one is the ENGINEER-facing story and comes from
 * the browser SDK, so it still fires for the failures that never reach a status callback at all —
 * a `<Dial>` the carrier rejects outright kills the browser leg before any call leg exists, which
 * is exactly the case that left no server-side trace.
 */
export interface SoftphoneTerminationDiagnostic {
  kind: 'termination';
  /** The terminal call event, or `device-error` for a failure on the signaling client itself. */
  event: 'disconnect' | 'cancel' | 'reject' | 'error' | 'device-error';
  /** Our correlation id for the call, when one exists (never set for device-level errors). */
  voiceCallId: string | null;
  /**
   * Whether the call was ever answered. A terminal event with this false is the instant-hangup
   * signature: the call died before media, which is what a rejected `<Dial>` looks like in the
   * browser.
   */
  becameActive: boolean;
  /**
   * Whether this is worth reporting: an `error` event, any carrier code, or a call that ended
   * without ever becoming active. A normal hangup after a real conversation is false.
   */
  abnormal: boolean;
  /**
   * The call's media quality, or null when the client doesn't measure it (device-level errors,
   * carriers whose SDK exposes no stats). A normal hangup with `quality.degraded` is the "audio
   * cut out mid-call" report that `abnormal` alone never catches.
   */
  quality: SoftphoneCallQuality | null;
  /** Carrier error code when the SDK supplied one (e.g. Twilio 31000). */
  code?: number;
  description?: string;
  explanation?: string;
  message?: string;
}

/**
 * A mid-call media event, emitted as it happens so the surface can build a timeline of the call
 * (the dashboard records each one as a Sentry breadcrumb) and, later, warn the rep in the UI.
 *
 *  - `warning` / `warning-cleared`: one of the SDK's network-quality or audio-level warnings
 *    (`high-packet-loss`, `high-jitter`, `high-rtt`, `low-mos`, `ice-connectivity-lost`,
 *    `constant-audio-input-level` = the rep's mic went flat). `name` carries which.
 *  - `reconnecting` / `reconnected`: the media or signaling path dropped and the SDK is
 *    renegotiating — both parties hear silence until `reconnected`.
 */
export interface SoftphoneQualityDiagnostic {
  kind: 'quality';
  event: 'warning' | 'warning-cleared' | 'reconnecting' | 'reconnected' | 'device-change';
  voiceCallId: string;
  /** The SDK's warning name for `warning` / `warning-cleared`; null for the other events. */
  name: string | null;
  /** Seconds since the call was placed, so a timeline reads in call time rather than wall time. */
  secondsIntoCall: number;
  /**
   * The threshold that tripped and the measured value(s) for a warning; for `device-change`, the
   * labels of the devices the call was using that just disappeared (empty when a device was only
   * added or an unused one removed). Null when the SDK supplied nothing.
   */
  detail: { threshold?: { name: string; value: number }; values?: number[]; lostDevices?: string[] } | null;
}

/**
 * The call never got as far as a Call object: one of the steps that set it up threw. Which step
 * is the whole diagnosis — `dial` is where the browser asks for the microphone, so a denied or
 * missing mic lands here as `dial`, which no other diagnostic can see because there is no call
 * to attach it to.
 *
 *  - `token`: minting the browser token (tRPC `voiceCall.getClientToken`).
 *  - `register`: connecting the signaling client with that token (`SoftphoneClient.init`).
 *  - `server`: creating the server-side call (tRPC `voiceCall.startCall`). Usually a refusal the
 *    server already logged (no number, calling disabled), so the surface reports it quietly.
 *  - `dial`: placing the call (`SoftphoneClient.dial`), including the mic permission prompt. The
 *    Twilio SDK resolves `connect()` before it asks for the mic and reports a refusal as a
 *    UserMedia error (31401 / 31402) on the never-answered call; the client maps that here.
 */
export interface SoftphoneStartFailureDiagnostic {
  kind: 'start-failure';
  stage: 'token' | 'register' | 'server' | 'dial';
  /** Our correlation id, known once `server` succeeded; null before that. */
  voiceCallId: string | null;
  message: string;
  /** The thrown error's `name` (e.g. `NotAllowedError` for a denied mic), or null for non-Errors. */
  errorName: string | null;
  /** The carrier's error code when the thrown error carried one (e.g. Twilio 31208 UserMediaAccessDenied). */
  code: number | null;
}

export type SoftphoneDiagnostic =
  | SoftphoneTerminationDiagnostic
  | SoftphoneQualityDiagnostic
  | SoftphoneStartFailureDiagnostic;

export interface SoftphoneCallHandlers {
  onStatus: (status: SoftphoneStatus) => void;
  onError: (message: string) => void;
  /**
   * Optional diagnostic sink (see `SoftphoneDiagnostic`): how each call ended, plus the mid-call
   * quality events. Optional so a surface that has nowhere to send them simply omits it rather
   * than passing a no-op.
   */
  onDiagnostic?: (diagnostic: SoftphoneDiagnostic) => void;
  /**
   * Mint a fresh signaling token when the current one is about to expire (Twilio's
   * `tokenWillExpire`, 10s before the hour-long token lapses). Resolves the new token; the client
   * swaps it in without re-registering, so a Device left registered across calls, or a call that
   * outlives its token, keeps its signaling connection. Omitted = the Device unregisters on expiry
   * and the next `init` rebuilds it.
   */
  onTokenWillExpire?: () => Promise<string>;
  /**
   * A short instruction for the rep while a call is being set up (e.g. "Sign in to JustCall…"),
   * or null to clear it. Only clients that need the rep's help to place a call emit these.
   */
  onNotice?: (notice: string | null) => void;
  /**
   * The carrier's own id for the call being placed, once it's known (`ended: false`) and again
   * when the call ends (`ended: true`). Only emitted by clients whose carrier can't carry our
   * correlation id (JustCall) — the surface forwards it to `voiceCall.reportProviderCall` so the
   * server can pull the call's result itself.
   */
  onProviderCall?: (event: { providerCallId: string; ended: boolean }) => void;
}

export interface SoftphoneDialParams {
  destinationNumber: string;
  callerNumber: string | null;
  voiceCallId: string;
}

/**
 * Platform mic capability — the injectable seam. The dashboard leaves this undefined and
 * lets the SDK call `getUserMedia` directly; the extension supplies a stream captured in
 * its offscreen document (where a persistent mic is allowed under MV3).
 */
export interface SoftphoneMediaStrategy {
  getInputStream?: () => Promise<MediaStream>;
}

export interface SoftphoneClient {
  /**
   * True when the carrier's own dialer runs the call (JustCall's embedded iframe): mute, keypad
   * and hang-up live there, so the dialer UI hides ours and `hangup`/`setMuted`/`sendDigits` do
   * nothing. Absent / false for carriers we drive directly (Twilio, Telnyx).
   */
  readonly embeddedCarrierDialer?: boolean;
  /** Register lifecycle handlers. Call before `init`/`dial`. */
  on(handlers: SoftphoneCallHandlers): void;
  /** Connect the signaling client with a freshly minted token. */
  init(token: string): Promise<void>;
  /** Place the outbound call. */
  dial(params: SoftphoneDialParams): Promise<void>;
  hangup(): void;
  setMuted(muted: boolean): void;
  /**
   * Send DTMF tones on the live call (IVR navigation). Accepts `0-9`, `*`, `#`.
   * No-op when no call is active (parity with hangup/setMuted). May throw on providers that
   * reject digits before media is established — callers guard with try/catch.
   */
  sendDigits(digits: string): void;
  /**
   * End the current call and release its per-call resources (disconnect, detach the call's
   * listeners, clear the active call) WITHOUT tearing down the signaling client/device — so the
   * next dial reuses the already-registered connection. Idempotent; safe with no active call.
   */
  closeCall(): void;
  /** Tear down the call, signaling connection, and any DOM/audio resources. */
  destroy(): Promise<void>;
}
